import type { ComponentProps } from 'react';
import { Input } from '@/components/ui/input';
import {
  maskCep,
  maskCnpj,
  maskCpf,
  maskDecimal,
  maskInteger,
  maskMoney,
  maskPhone,
} from '@/lib/masks';

/** Tipo do campo; cada um com a sua máscara e o teclado certo no celular. */
export type MaskKind =
  | 'money'
  | 'percent'
  | 'measure'
  | 'decimal'
  | 'integer'
  | 'code'
  | 'cnpj'
  | 'cpf'
  | 'phone'
  | 'cep';

const MASKS: Record<
  MaskKind,
  {
    apply: (value: string) => string;
    inputMode: 'decimal' | 'numeric' | 'tel' | 'text';
    placeholder?: string;
  }
> = {
  money: { apply: maskMoney, inputMode: 'numeric', placeholder: '0,00' },
  percent: {
    apply: (value) => maskDecimal(value, { integer: 2, fraction: 2 }),
    inputMode: 'decimal',
  },
  measure: {
    apply: (value) => maskDecimal(value, { integer: 6, fraction: 2 }),
    inputMode: 'decimal',
  },
  decimal: {
    apply: (value) => maskDecimal(value, { integer: 9, fraction: 6 }),
    inputMode: 'decimal',
  },
  integer: { apply: (value) => maskInteger(value), inputMode: 'numeric' },
  code: { apply: (value) => maskInteger(value, 6), inputMode: 'numeric', placeholder: '000000' },
  // CNPJ alfanumérico tem letra: teclado de texto.
  cnpj: { apply: maskCnpj, inputMode: 'text', placeholder: '00.000.000/0000-00' },
  cpf: { apply: maskCpf, inputMode: 'numeric', placeholder: '000.000.000-00' },
  phone: { apply: maskPhone, inputMode: 'tel', placeholder: '(00) 00000-0000' },
  cep: { apply: maskCep, inputMode: 'numeric', placeholder: '00000-000' },
};

/**
 * Campo com máscara, obrigatório para todo dado com formato (ADR-055): dinheiro (1.000,00),
 * percentual, medida, CNPJ, CPF, telefone, CEP, inteiro, código. É o `Input` do shadcn/ui — nada
 * visual novo —, com a digitação formatada e o teclado certo no celular. O lint impede `inputMode`
 * numérico solto num `Input`: campo desses passa por aqui.
 *
 * O valor que sai é o texto mascarado; a conversão para a API é de `lib/schemas.ts`. Valor
 * monetário vindo da API entra com `moneyToMasked` (sempre duas casas).
 */
export function MaskedInput({
  mask,
  value,
  onChange,
  placeholder,
  ...props
}: Omit<ComponentProps<typeof Input>, 'value' | 'onChange' | 'inputMode'> & {
  mask: MaskKind;
  value: string | null | undefined;
  onChange: (value: string) => void;
}) {
  const config = MASKS[mask];
  return (
    <Input
      {...props}
      value={value ?? ''}
      inputMode={config.inputMode}
      placeholder={placeholder ?? config.placeholder}
      onChange={(event) => onChange(config.apply(event.target.value))}
    />
  );
}
